import { DialogActions, DialogTitle, SelectChangeEvent, Drawer, Alert } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { addressCountries, ReturnAddress, stateMapper } from '../../utils/constant';
import * as uuid from 'uuid';
import BkOverlay from '../overlay/BkOverlay';
import StyledDialogContent from '../../pages/create-notice/styled/preferences-page-styles/add-address-dialog/StyledDialogContent';
import { TextField } from '../new/inputs/TextField';
import Select from '../new/inputs/Select';
import { Button } from '../new/Button';

interface ReturnAddressDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (returnAddress: ReturnAddress, action: 'edit' | 'create') => void;
  isEditing: boolean;
  itemToEdit: ReturnAddress | undefined;
  returnAddrStatus: { status: string } | null;
}

const AddReturnAddressDialog: React.FC<ReturnAddressDialogProps> = ({
  open,
  isEditing,
  itemToEdit,
  onClose,
  onSubmit,
  returnAddrStatus,
}) => {
  const [newReturnAddress, setNewReturnAddress] = useState<ReturnAddress>({
    firmName: '',
    addressOne: '',
    addressTwo: '',
    zipCode: '',
    city: '',
    state: '',
    addressName: '',
    country: 'United States',
    selected: false,
    isNew: true,
    isDefault: true,
    id: uuid.v4(),
  });

  const returnErrors = useSelector((state: RootState) => state.upload.returnAddressErrors);

  const courtStates = useSelector((state: RootState) => state.upload.courtStates);

  const onStateChange = (event: SelectChangeEvent<string>) => {
    setNewReturnAddress((prevState) => ({
      ...prevState,
      state: event.target.value,
    }));
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setNewReturnAddress((prevState) => ({ ...prevState, [name]: value }));
  };

  const onSubmitNewReturnAddress = () => {
    isEditing ? onSubmit(newReturnAddress, 'edit') : onSubmit(newReturnAddress, 'create');
  };

  const onSelectCountryChange = (e: SelectChangeEvent) => {
    setNewReturnAddress((prevState) => ({ ...prevState, country: e.target.value, state: '' }));
  };

  useEffect(() => {
    if (open && itemToEdit !== undefined) {
      setNewReturnAddress(itemToEdit);
    }

    return () => {
      if (!open) {
        setNewReturnAddress({
          firmName: '',
          addressOne: '',
          addressTwo: '',
          zipCode: '',
          city: '',
          state: '',
          country: 'United States',
          addressName: '',
          selected: false,
          isNew: true,
          id: uuid.v4(),
        });
      }
    };
  }, [open, isEditing]);

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={onClose}
      sx={{
        '& .MuiPaper-root': {
          background: '#FFF',
        },
      }}
    >
      <DialogTitle>{isEditing ? 'Edit Return Address' : 'Add Return Address'}</DialogTitle>
      <StyledDialogContent>
        {returnAddrStatus?.status === 'pending' && <BkOverlay overlayTitle="Adding address... Please wait!" />}
        {Object.values(returnErrors).some((err) => err !== '') && (
          <Alert severity="error" sx={{ marginBottom: '0.625rem', marginTop: '-1.25rem' }}>
            Please, add missing information
          </Alert>
        )}
        <form>
          <TextField
            label="Name"
            value={newReturnAddress.addressName}
            name="addressName"
            id="addressName"
            autoComplete="name"
            required
            placeholder="Add..."
            error={returnErrors.addressName}
            onChange={onChange}
          />
          <TextField
            label="Firm Name"
            value={newReturnAddress.firmName}
            id="firmName"
            name="firmName"
            placeholder="Add..."
            error={returnErrors.addressName}
            onChange={onChange}
          />
          <TextField
            id="addressOne"
            label="Address 1"
            required
            value={newReturnAddress.addressOne}
            placeholder="Add..."
            autoComplete="address-line1"
            name="addressOne"
            error={returnErrors.addressOne}
            onChange={onChange}
          />
          <TextField
            id="addressTwo"
            label="Address 2"
            value={newReturnAddress.addressTwo ? newReturnAddress.addressTwo : ''}
            name="addressTwo"
            placeholder="Add..."
            error=""
            onChange={onChange}
          />
          <Select
            data={addressCountries}
            name="country"
            value={newReturnAddress.country}
            containerSx={{ marginBottom: '1.5rem' }}
            onChange={onSelectCountryChange}
            error=""
            disabled={false}
            label="Country"
            required
          />
          <div
            style={{
              display: 'flex',
              gap: '0.625rem',
              width: '100%',
            }}
          >
            <TextField
              id="city"
              label="City"
              value={newReturnAddress.city}
              autoComplete="address-level2"
              name="city"
              containerSX={{ flex: 2 }}
              placeholder="Add..."
              required
              error={returnErrors.city}
              onChange={onChange}
            />
            {newReturnAddress.country === 'United States' ? (
              <Select
                label="State"
                required
                name="state"
                autoComplete="address-level1"
                value={newReturnAddress.state}
                error={returnErrors.state}
                placeholder="Add..."
                containerSx={{ flex: 1 }}
                data={Object.values(stateMapper)}
                disabled={false}
                onChange={onStateChange}
              />
            ) : (
              <TextField
                label="State"
                required
                name="state"
                id="state"
                autoComplete="address-level1"
                value={newReturnAddress.state}
                containerSX={{ flex: 1 }}
                placeholder="Add..."
                onChange={onStateChange}
                error={returnErrors.state}
              />
            )}
            <TextField
              label={newReturnAddress.country === 'Canada' ? 'Postal code' : 'ZIP'}
              required
              containerSX={{ flex: 1 }}
              id="zipCode"
              value={newReturnAddress.zipCode}
              autoComplete="postal-code"
              name="zipCode"
              placeholder="Add..."
              error={returnErrors.zipCode}
              onChange={onChange}
            />
          </div>
        </form>
      </StyledDialogContent>
      <DialogActions sx={{ width: '100%' }}>
        <Button type="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="primary" onClick={onSubmitNewReturnAddress}>
          Save
        </Button>
      </DialogActions>
    </Drawer>
  );
};

export default AddReturnAddressDialog;
